<script lang="ts">
  import type { HTMLAttributes } from 'vue'
  import type { DateCalendarProps } from '../date-calendar'
  import type { DatePickerAnchorTime } from '../date-input'

  /** Props for `DatePicker`. */
  export interface DatePickerProps {
    /** `id` for the trigger button — wire to a `<label for>` for a11y. */
    id?: string
    /** Label shown inside the field, above the selected date. */
    label?: string
    /** Text shown when no date is selected. */
    placeholder?: string
    disabled?: boolean
    /** Renders the destructive error ring; wire to a form field's invalid state. */
    ariaInvalid?: boolean
    /**
     * Time-of-day a picked date is anchored to. `'noon'` keeps the value
     * on the same calendar day across timezones. Defaults to `'noon'`.
     */
    anchorTime?: DatePickerAnchorTime
    /** Date ranges to disable in the calendar. */
    disabledDateRanges?: DateCalendarProps['disabledRanges']
    /** Individual dates to disable in the calendar. */
    disabledDates?: DateCalendarProps['disabledDates']
    dataTestid?: string
    /** Class applied to the field-box trigger. */
    class?: HTMLAttributes['class']
  }
</script>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import type { ISODateTimeString } from '@gat-ship/types'
  import { IconChevronDown } from '@gat-ship/icons'
  import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
  import { DateCalendar } from '../date-calendar'
  import { useDatePicker } from '../date-input'
  import { useUserPreferences } from '../../composables/useUserPreferences'
  import { cn } from '../../lib/utils'

  /**
   * Standalone calendar date picker. A bordered field-box trigger —
   * label inside on top, the selected date below — opens a popover
   * calendar; `v-model`-bound to an `ISODateTimeString`.
   *
   * The non-form counterpart of `FormDatePickerField`. Date conversion
   * and anchoring are delegated to `useDatePicker`.
   */
  const props = withDefaults(defineProps<DatePickerProps>(), {
    anchorTime: 'noon',
    placeholder: 'Pick a date'
  })

  const model = defineModel<ISODateTimeString | undefined>()

  const open = ref(false)

  const getUserPreferences = useUserPreferences()

  const locale = computed(
    () =>
      getUserPreferences().dateFormat ??
      Intl.DateTimeFormat().resolvedOptions().locale
  )

  const { selectedDate, handleDateChange } = useDatePicker(
    {
      modelValue: model,
      timeZone: undefined,
      anchorTime: () => props.anchorTime
    },
    (value) => {
      model.value = value
    }
  )

  /** The selected date in the user's locale format, or `''` when empty. */
  const displayValue = computed(() => {
    const date = selectedDate.value
    if (!date) return ''
    return date
      .toLocaleString(locale.value, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      .replace(',', '')
  })

  // Commit the pick and close the popover.
  function onCalendarChange(date: Parameters<typeof handleDateChange>[0]) {
    handleDateChange(date)
    open.value = false
  }
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <button
        :id="id"
        type="button"
        :disabled="disabled"
        :data-testid="dataTestid"
        :aria-invalid="ariaInvalid || undefined"
        :class="
          cn(
            'border-input bg-background focus-visible:border-ring focus-visible:ring-ring/50 flex w-full flex-col items-start gap-0.5 rounded-md border px-3 py-1.5 text-left text-sm outline-none transition-colors focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
            ariaInvalid && 'border-destructive ring-destructive/20',
            props.class
          )
        "
      >
        <span
          v-if="label"
          class="text-xs font-medium leading-none text-neutral-700 dark:text-neutral-200"
        >
          {{ label }}
        </span>
        <span class="flex w-full items-center gap-2">
          <span
            :class="
              displayValue ? '' : 'text-neutral-400 dark:text-neutral-500'
            "
          >
            {{ displayValue || placeholder }}
          </span>
          <IconChevronDown
            class="text-muted-foreground ml-auto size-3.5 shrink-0"
          />
        </span>
      </button>
    </PopoverTrigger>
    <PopoverContent as-child align="start">
      <DateCalendar
        mode="single"
        :model-value="selectedDate"
        :disabled-dates="disabledDates"
        :disabled-ranges="disabledDateRanges"
        @update:model-value="onCalendarChange"
      />
    </PopoverContent>
  </Popover>
</template>
